import { useState } from 'react';
import type { ChecklistItem, ChecklistStatus } from '../../api/types';
import { useI18n, useT } from '../../i18n';
import { loc } from '../../lib/loc';
import { UploadControl } from '../upload/UploadControl';
import { useUploads } from '../upload/uploadStore';
import { badgeKey, clientItems, openItems, rejectReason } from './model';
import { dueByText } from './dueBy';

// кружечок-позначка біля кожного статусу; червоний лише для «Потрібно переробити»
const MARK: Record<Exclude<ChecklistStatus, 'not_needed'>, string> = {
  missing: 'mk-next',
  uploaded: 'mk-cur',
  accepted: 'mk-done',
  rejected: 'mk-bad',
};

export function StatusBadge({ status, required = true }: { status: ChecklistStatus; required?: boolean }) {
  const t = useT();
  const mk = MARK[status as keyof typeof MARK] ?? MARK.missing;
  const optional = status === 'missing' && !required;
  return (
    <span className={`inline-flex items-center gap-2 whitespace-nowrap text-[13px] ${status === 'rejected' ? 'text-bad' : status === 'accepted' ? 'text-fg' : optional ? 'text-lab' : 'text-txt'}`}>
      <i className={`mk ${mk}`} aria-hidden />
      {t(badgeKey({ status, required }))}
    </span>
  );
}

export function NeedsSection({ caseId, checklist, onChanged }: { caseId: string; checklist: ChecklistItem[]; onChanged: () => void }) {
  const t = useT();
  const locale = useI18n((s) => s.locale);
  const items = clientItems(checklist);
  const open = openItems(checklist);
  const active = useUploads((s) => s.entries);

  return (
    <section aria-labelledby="needs-h" id="needs" className="scroll-mt-6">
      <h2 id="needs-h" className="sec-h">
        {t('case.needs')}
        {open.length ? <small>{t('case.needsCount', { n: open.length })}</small> : null}
      </h2>
      {open.length === 0 ? (
        <p className="mt-5 text-[16px] text-txt">{t('case.needsDone')}</p>
      ) : (
        <p className="mt-4 text-[15px] leading-relaxed text-txt">{t('case.needsIntro')}</p>
      )}
      <ul className="mt-4">
        {items.map((i) => {
          const name = loc(i.document_type?.name, locale) || i.document_type?.key;
          const hint = loc(i.document_type?.client_hint, locale);
          const needsUpload = i.status === 'missing' || i.status === 'rejected' || !!active[i.id];
          const reason = rejectReason(i);
          // до коли родина має дати документ (047) — лише поки його ще чекаємо
          const expected = (i as ChecklistItem & { expected_at?: string | null }).expected_at;
          const due = i.status === 'missing' || i.status === 'rejected' ? dueByText(expected, locale) : null;
          return (
            <li key={i.id} className="row py-5">
              <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
                <h3 className="min-w-0 flex-1 text-[17px] font-normal leading-snug">{name}</h3>
                <StatusBadge status={i.status} required={i.required} />
              </div>
              {hint ? <p className="mt-1 text-[14px] leading-relaxed text-txt">{hint}</p> : null}
              {due ? <p className="mt-1 text-[14px] text-fg">{due}</p> : null}
              {i.status === 'rejected' ? (
                <p className="mt-3 border-l-2 border-bad pl-3 text-[15px]">{reason ? t('case.rejectedReason', { reason }) : t('case.rejectedNoReason')}</p>
              ) : null}
              {needsUpload ? (
                <UploadControl
                  itemId={i.id}
                  caseId={caseId}
                  documentTypeKey={i.document_type?.key}
                  label={name ?? ''}
                  replace={i.status === 'rejected'}
                  onDone={onChanged}
                />
              ) : i.status === 'uploaded' && i.can_upload !== false ? (
                <ReplaceUpload itemId={i.id} caseId={caseId} documentTypeKey={i.document_type?.key} label={name ?? ''} onDone={onChanged} />
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** For a file that is waiting for review: a quiet way to send a corrected one, hidden until asked for. */
function ReplaceUpload(props: { itemId: string; caseId: string; documentTypeKey?: string; label: string; onDone: () => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  if (open) return <UploadControl {...props} replace onDone={() => { setOpen(false); props.onDone(); }} />;
  return (
    <div className="mt-2">
      <p className="text-[13px] text-lab">{t('case.replaceHint')}</p>
      <button type="button" className="link" onClick={() => setOpen(true)}>
        {t('upload.replace')}
      </button>
    </div>
  );
}
